import { useEffect, useRef, useState } from 'react';
import usePrefersReducedMotion from './usePrefersReducedMotion';

/**
 * Reports when an element first enters the viewport.
 *
 * Defaults to `true` when IntersectionObserver is unavailable or motion is
 * reduced, so content is never left hidden by a missing API or a user
 * preference. Reveal-on-scroll must degrade to plain visible content, never to
 * a blank page.
 */
export default function useInView({ rootMargin = '0px 0px -10% 0px' } = {}) {
  const prefersReduced = usePrefersReducedMotion();
  const ref = useRef(null);
  const [inView, setInView] = useState(
    () => prefersReduced || typeof IntersectionObserver === 'undefined'
  );

  useEffect(() => {
    if (prefersReduced || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }

    const node = ref.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // One-way: once revealed it stays revealed, so scrolling back up does
        // not replay the animation.
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      // threshold must stay 0. `intersectionRatio` is measured against the
      // target's own area, so a block much taller than the viewport can never
      // reach a fractional threshold: the announcements feed renders ~45000px
      // of Discord posts, and at a 900px viewport its ratio peaks around 0.018.
      // A 0.05 threshold left that section permanently at opacity 0. The
      // negative bottom rootMargin above already supplies the "meaningfully in
      // view" delay that a threshold would otherwise give us, without tying it
      // to how tall the element happens to be.
      { rootMargin, threshold: 0 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [prefersReduced, rootMargin]);

  return [ref, inView];
}
